/* eslint-disable react-hooks/rules-of-hooks */
import { useApi } from "@core/api";
import { useSubmit } from "@remix-run/react";
import _ from "lodash";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ForgotPasswordRequest, LoginRequest, ResetPasswordRequest } from "@/apidtos";
import { currentUserHookQueryKey } from "@core/hooks/useCurrentUser";

export interface ExtendedLoginRequest extends LoginRequest {
  skipSubmit?: boolean;
}

interface AuthActionArgs<T> {
  body?: T;
  onErrorCallback?: (error: unknown) => void;
  onSuccessCallback?: (data?: unknown, variables?: unknown, context?: unknown) => void;
}

export const useAuthActions = () => {
  const submit = useSubmit();
  const api = useApi();
  const queryClient = useQueryClient();

  const onSuccess = (data: unknown, variables: AuthActionArgs<unknown>, context: unknown) => {
    if (_.isFunction(variables?.onSuccessCallback)) {
      variables.onSuccessCallback(data, variables, context)
    }
  };

  const onError = (
    caller: string,
    error: unknown,
    variables: AuthActionArgs<unknown>
  ) => {
    console.error(`Error in ${caller}: ${(error as Error)?.message}`);
    console.info(error);
    if (_.isFunction(variables?.onErrorCallback)) {
      variables.onErrorCallback(error);
    }
  };

  const forgotPassword = useMutation({
    mutationFn: async (data: AuthActionArgs<ForgotPasswordRequest>) => {
      return api["/auth/forgot-password"](data.body!);
    },
    onSuccess,
    onError: (error, variables) => {
      onError("forgotPassword", error, variables);
    },
  });

  const resetPassword = useMutation({
    mutationFn: async (data: AuthActionArgs<ResetPasswordRequest>) => {
      return api["/auth/reset-password"](data.body!);
    },
    onSuccess,
    onError: (error, variables) => {
      onError("resetPassword", error, variables);
    },
  });

  const hasPermission = (permissionName: string) => {
    return queryClient.fetchQuery({
      queryKey: ["hasPermission", permissionName],
      queryFn: () => api["/auth/perm-check/{permission}"](permissionName),
    })
  };

  const hasRole = (roleName: string) => {
    return queryClient.fetchQuery({
      queryKey: ["hasRole", roleName],
      queryFn: () => api["/auth/role-check/{role}"](roleName),
    })
  };

  const login = useMutation({
    mutationFn: async (data: AuthActionArgs<ExtendedLoginRequest>) => {
      return api["/auth/login"](data.body!);
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: currentUserHookQueryKey });
      queryClient.invalidateQueries({ queryKey: ["emulation"] });
      if (variables.body && !variables.body.skipSubmit) {
        // This is just to trigger loaders to run, in case a login happens on a page that does something in the loader with user
        submit({}, { action: "/auth", method: "post", navigate: false });
      }
      onSuccess(data, variables, context);
    },
    onError: (error: unknown, variables) => {
      onError("login", error, variables);
    },
  });

  const logout = useMutation({
    mutationFn: async (_data: AuthActionArgs<void>) => {
      return api["/auth/logout"]();
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      // This is just to trigger loaders to run, in case a logout happens on a restricted page
      submit({}, { action: "/auth", method: "post", navigate: false });
      onSuccess(data, variables, context);
    },
    onError: (error: unknown, variables) => {
      onError("logout", error, variables);
    },
  });

  const updatePassword = useMutation({
    mutationFn: async (data: AuthActionArgs<{ Current: string; New: string }>) => {
      return api["/auth/current/password"](data.body!);
    },
    onSuccess,
    onError: (error: unknown, variables) => {
      onError("updatePassword", error, variables);
    },
  });

  return {
    hasPermission,
    hasRole,
    forgotPassword,
    resetPassword,
    login,
    logout,
    updatePassword
  };
};
